import React, { useEffect, useState } from "react";

import AccountSubHeader from "../account/accountSubHeader/accountSubheader";
import StatCircle from "../statCircle/StatCircle";
import { UserProfile } from "../../context/types";
import { ProductService } from "../../services/product.service";

import styles from "./statistics.module.css";

interface Props {
  userProfile: UserProfile;
  plan: { option: string; optionPrice: string; optionLimit: string | number };
}
const Statistics: React.FC<Props> = ({ userProfile, plan }) => {
  const [accountAgeData, setAccountAgeData] = useState<{
    title: string;
    message: string;
  }>(null);
  const [itemsWorn, setItemsWorn] = useState<number[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(null);

  useEffect(() => {
    setAccountAgeData(calculateAccountAge(userProfile?.created_at));
    setItemsWorn(calculateNoOfItems(userProfile?.previousProductVariants));
  }, [userProfile]);

  const calculateNoOfItems = (itemArray: number[]) => {
    //finds number of items worn, filters out duplicate values and removes null values
    return [...new Set(itemArray)].filter((item) => item !== null);
  };

  // Fetch products by variant IDs and calculate total price
  useEffect(() => {
    const calculatePriceOfItems = async () => {
      if (!itemsWorn || itemsWorn.length === 0) {
        setTotalPrice(0);
        return;
      }

      try {
        const products = await ProductService.getProductsByVariantIds(itemsWorn);

        let total = 0;
        itemsWorn.forEach((variantId) => {
          const product = products.find((prod) =>
            prod.product_variants.some((variant) => variant.id === variantId)
          );
          if (product) {
            total += product.price || 0;
          }
        });

        setTotalPrice(total);
      } catch (error) {
        console.error("Error calculating price:", error);
        setTotalPrice(null);
      }
    };

    if (itemsWorn?.length > 0) {
      calculatePriceOfItems();
    }
  }, [itemsWorn]);

  const calculateAccountAge = (startDate: string) => {
    const now = new Date();
    const start = new Date(startDate);

    const diff = Math.floor(now.getTime() - start.getTime());
    const day = 1000 * 60 * 60 * 24;

    let days = Math.floor(diff / day);
    let months = Math.floor(days / 31);
    let years = Math.floor(months / 12);
    days = days - months * 32;

    const calculateMessage = (days: string, months: string, years: string) => {
      if (years === "0") {
        return `You've been with us for ${months} months and ${days} days`;
      } else {
        return `You've been with us for ${years} ${
          years === "1" ? "year" : "years"
        } ${months !== "0" ? `,${months}` : ""} and ${days} days`;
      }
    };
    const calculateTitle = (months: string, years: string) => {
      if (years === "0") {
        if (Number(months) < 10) {
          return `0${months}`;
        } else {
          return months;
        }
      } else {
        return years;
      }
    };

    return {
      message: calculateMessage(
        days.toString(),
        months.toString(),
        years.toString()
      ),
      title: calculateTitle(months.toString(), years.toString()),
    };
  };

  return (
    <>
      <AccountSubHeader title={"Your rental facts"} />
      <div className={styles.statsContainer}>
        <StatCircle
          title={`Plan ${plan?.option}`}
          text={`${plan?.optionLimit} pieces each month`}
          boldText={plan?.optionPrice}
          color={"var(--colorOrange)"}
          noPlan={!plan?.option}
        />
        <StatCircle
          title={accountAgeData?.title}
          text={accountAgeData?.message}
          color={"var(--colorGreySand)"}
        />
        <StatCircle
          title={itemsWorn.length.toString()}
          text={`Garments have been worn by ${
            userProfile?.babyName ? userProfile?.babyName : "your child"
          }`}
          color={"var(--colorRajah)"}
        />
        <StatCircle
          title={`£${
            Number.isNaN(totalPrice) || totalPrice === null ? "TBC" : totalPrice
          }`}
          text={"This would have cost, when brought new"}
          color={"var(--colorQuicksand"}
        />
      </div>
    </>
  );
};

export default Statistics;
